import React from 'react';

/**
 * WHERE THE ORDER IS GOING — drawn once, at the foot of shipping.
 *
 * The warehouse's part of this journey ends at a tracking number. Delivery and
 * going live are confirmed on the two stages after it, by somebody at the
 * store, and nothing on this screen can observe either. So the rail stops here
 * with the destination rather than with another control: the last thing an
 * operator does is hand the boxes to a carrier.
 *
 * INLINE SVG, not a render. It scales to any card width, costs one request
 * (none, in fact), and reads in both the light chrome this app uses and a
 * print. If a photoreal hero is ever produced, it drops in over the top as a
 * transparent PNG at `/brand/summit-hero.png` — the illustration stays
 * underneath as the fallback, so a missing file is never a broken image.
 *
 * The mark is the branding pack's own animated logo
 * (`03 Animation/Web and email/rubytill-logo-animated-light.svg`), served from
 * `public/brand/` with its opaque white plate removed and nothing else changed
 * — that plate is baked into the source file and renders as a white box over
 * artwork.
 */
const DestinationMark: React.FC = () => (
  <div className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white">
    <div className="relative">
      <svg
        viewBox="0 0 800 340"
        className="block h-auto w-full"
        role="img"
        aria-label="Two figures at the summit of a mountain beside a checkered flag"
      >
        <defs>
          <linearGradient id="dm-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fde9ee" />
            <stop offset="46%" stopColor="#FFF6F7" />
            <stop offset="100%" stopColor="#fff" />
          </linearGradient>
          <radialGradient id="dm-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#DC143C" stopOpacity=".34" />
            <stop offset="60%" stopColor="#DC143C" stopOpacity=".10" />
            <stop offset="100%" stopColor="#DC143C" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="dm-lit" x1="0" y1="0" x2=".7" y2="1">
            <stop offset="0%" stopColor="#8c8480" />
            <stop offset="55%" stopColor="#5f5853" />
            <stop offset="100%" stopColor="#413b37" />
          </linearGradient>
          <linearGradient id="dm-shade" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#37312e" />
            <stop offset="100%" stopColor="#1c1917" />
          </linearGradient>
          <linearGradient id="dm-far" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#cfc9c5" />
            <stop offset="100%" stopColor="#efeceb" />
          </linearGradient>
          <linearGradient id="dm-haze" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0" />
            <stop offset="100%" stopColor="#fff" stopOpacity=".92" />
          </linearGradient>
          <linearGradient id="dm-snow" x1="0" y1="0" x2=".6" y2="1">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="100%" stopColor="#e7e2df" />
          </linearGradient>
        </defs>

        <rect width="800" height="340" fill="url(#dm-sky)" />
        <circle cx="648" cy="82" r="72" fill="url(#dm-glow)" />
        <circle cx="648" cy="82" r="23" fill="#DC143C" opacity=".85" />

        {/* distant range, hazed back so the peak reads as the near one */}
        <path d="M0 340 L138 150 L232 236 L330 140 L462 340Z" fill="url(#dm-far)" />
        <path d="M452 340 L604 158 L688 240 L800 168 L800 340Z" fill="url(#dm-far)" opacity=".8" />
        <rect y="150" width="800" height="150" fill="url(#dm-haze)" />

        {/* the peak — a lit face and a shadow face, so it has form */}
        <path d="M118 340 L400 48 L400 340Z" fill="url(#dm-lit)" />
        <path d="M400 48 L690 340 L400 340Z" fill="url(#dm-shade)" />
        <path d="M400 48 L400 340" stroke="#fff" strokeOpacity=".07" strokeWidth="2" />
        <path d="M400 48 L470 120 L440 142 L412 118 L400 128Z" fill="#efeae7" />
        <path d="M400 48 L400 128 L388 118 L356 146 L330 120Z" fill="url(#dm-snow)" />
        <path d="M243 195 L268 228 L228 226Z" fill="#fff" opacity=".13" />
        <path d="M505 210 L534 250 L486 246Z" fill="#000" opacity=".16" />
        <ellipse cx="404" cy="132" rx="52" ry="7" fill="#1c1917" opacity=".22" />

        {/* the flag */}
        <g transform="translate(455 44)">
          <rect x="0" y="0" width="4" height="96" rx="2" fill="#241f1d" />
          <path d="M4 3 h62 v36 h-62Z" fill="#fff" />
          <g fill="#1c1917">
            <rect x="4" y="3" width="15.5" height="9" />
            <rect x="35" y="3" width="15.5" height="9" />
            <rect x="19.5" y="12" width="15.5" height="9" />
            <rect x="50.5" y="12" width="15.5" height="9" />
            <rect x="4" y="21" width="15.5" height="9" />
            <rect x="35" y="21" width="15.5" height="9" />
            <rect x="19.5" y="30" width="15.5" height="9" />
            <rect x="50.5" y="30" width="15.5" height="9" />
          </g>
          <path d="M4 3 h62 v36 h-62Z" fill="none" stroke="#241f1d" strokeWidth="1.5" />
        </g>

        {/* the two of them, arms up, hands met */}
        <g transform="translate(360 86)">
          <g fill="#1c1917">
            <circle cx="14" cy="13" r="8.5" />
            <path d="M6 24h16l4 26H2z" />
            <path d="M8 50h6l1 22h-7zM16 50h6l2 22h-7z" />
            <path d="M6.5 26 L-6 6l4.5-3 13 19zM21.5 26 L34 6l-4.5-3-13 19z" />
          </g>
          <g fill="#DC143C" transform="translate(36 2)">
            <circle cx="14" cy="13" r="8.5" />
            <path d="M5 24h18l3 26H2z" />
            <path d="M8 50h6l1 20h-7zM16 50h6l2 20h-7z" />
            <path d="M6.5 26 L-6 7l4.5-3 13 18zM21.5 26 L34 7l-4.5-3-13 18z" />
          </g>
          <path d="M30 4 L40 4" stroke="#241f1d" strokeWidth="3.5" strokeLinecap="round" />
        </g>
      </svg>

      {/* A rendered hero, if one is ever dropped in. Removed on error so the
          illustration below stands on its own. */}
      <img
        src="/brand/summit-hero.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full object-contain"
        style={{ objectPosition: 'center 62%' }}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />

      <img
        src="/brand/rubytill-logo-animated.svg"
        alt="RubyTill"
        className="absolute left-5 top-4 w-[150px] max-w-[30%] drop-shadow-[0_1px_6px_rgba(255,255,255,0.85)]"
      />
    </div>

    <div className="px-4 pb-4 pt-3 text-center">
      <p className="text-[15px] font-extrabold tracking-[-0.01em] text-stone-900">Live at the store</p>
      <p className="mx-auto mt-1 max-w-[540px] text-[12px] leading-relaxed text-stone-500">
        Where the order is going. Delivery and going live are confirmed after this — the warehouse&apos;s
        part finishes at the tracking number.
      </p>
    </div>
  </div>
);

export default DestinationMark;
